import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const STAFF_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: "🏠" },
  { to: "/rentals", label: "Verleihe", icon: "📋" },
  { to: "/items", label: "Inventar", icon: "🎿" },
];

const ADMIN_NAV = [
  { to: "/admin/equipment", label: "Modelle", icon: "🏷️" },
  { to: "/admin/locations", label: "Standorte", icon: "📍" },
  { to: "/admin/users", label: "Mitarbeiter", icon: "👥" },
  { to: "/admin/qr-catalog", label: "QR-Katalog", icon: "📇" },
  { to: "/admin/recap", label: "Recap", icon: "📊" },
  { to: "/admin/settings", label: "Einstellungen", icon: "⚙️" },
];

function NavItem({ to, label, icon, primary, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      end={to === "/dashboard"}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm transition-colors",
          primary && !isActive ? "bg-fh-red text-white hover:bg-fh-red-dark" : "",
          isActive
            ? "bg-fh-black text-white"
            : !primary
              ? "text-fh-black hover:bg-fh-gray-light"
              : "",
        ].join(" ")
      }
    >
      <span className="text-base leading-none">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const close = () => setMobileOpen(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-fh-gray-light">
      {/* Top bar (mobile) */}
      <header className="md:hidden bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <img src={`${process.env.PUBLIC_URL}/fh_logo_circle.png`} alt="" className="h-8 w-8 logo-clean" />
          <span className="font-semibold">Freeheeler</span>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 -mr-2 rounded-md hover:bg-fh-gray-light"
          aria-label="Menü"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={[
          "bg-white border-r border-gray-200 md:w-64 md:min-h-screen md:sticky md:top-0",
          mobileOpen ? "block" : "hidden md:block",
        ].join(" ")}
      >
        <div className="hidden md:flex items-center gap-3 px-5 h-16 border-b border-gray-200">
          <img src={`${process.env.PUBLIC_URL}/fh_logo_circle.png`} alt="" className="h-9 w-9 logo-clean" />
          <div>
            <div className="font-bold text-base leading-tight">Freeheeler</div>
            <div className="text-xs text-fh-gray">Verleih-Portal</div>
          </div>
        </div>

        <nav className="p-3 space-y-1">
          <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fh-gray">
            Verleih
          </div>
          {STAFF_NAV.map((item) => (
            <NavItem key={item.to} {...item} onClick={close} />
          ))}

          {isAdmin && (
            <>
              <div className="px-2 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fh-gray">
                Verwaltung
              </div>
              {ADMIN_NAV.map((item) => (
                <NavItem key={item.to} {...item} onClick={close} />
              ))}
            </>
          )}
        </nav>

        <div className="p-3 mt-auto border-t border-gray-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-fh-black text-white flex items-center justify-center text-xs font-semibold">
              {user?.full_name?.split(" ").map((s) => s[0]).slice(0, 2).join("") || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`badge ${isAdmin ? "badge-admin" : "badge-staff"}`}>
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onLogout} className="btn-ghost w-full justify-start mt-1">
            Abmelden
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
